import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, List, Stack, Table, TableBody, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import PaperDraggable from "../../../../../../../../components/PaperDraggable";
import { TProdutosDetalhes } from "../../../../../../../../types/api/comercial/detalhar-pedido";
import { TableCellSemBorder } from "../../StepExpedicao";
import { Fragment, useMemo, useState } from "react";
import EditIcon from '@mui/icons-material/Edit';
import { IItensPorLojaDetalhes } from "../../../CardItens";
import { useMutation, useQueryClient } from "react-query";
import axiosClient from "../../../../../../../../lib/axios";
import { enqueueSnackbar } from "notistack";
import DialogQuantidadeExpedir from "./DialogQuantidadeExpedir";

export default function DialogExpedirItens({
    open,
    data,
    idPedido,
    handleClose,
}: DialogExpedirItensProps
) {
    const queryClient = useQueryClient();

    const [openQuantidadeDialog, setOpenQuantidadeDialog] = useState<TProdutosDetalhes | null>(null);
    const [itensParaExpedir, setItensParaExpedir] = useState<TProdutosDetalhes[]>(data);

    const itensPorLoja = useMemo(() => {
        return itensParaExpedir.reduce<Record<number, IItensPorLojaDetalhes>>((acc, item) => {
            const lojaId = item.loja.id;
            if (!acc[lojaId]) {
                acc[lojaId] = {
                    loja: item.loja,
                    itens: []
                };
            }
            acc[lojaId].itens.push(item);
            acc[lojaId].itens.sort((a, b) => a.nome.localeCompare(b.nome));
            return acc;
        }, {});
    }, [itensParaExpedir]);

    // Converte o objeto em array para facilitar o mapeamento no JSX
    const itensPerloja = Object.values(itensPorLoja);

    //Querys e mutations
    const { mutate: expedirItens, isLoading } = useMutation({
        mutationKey: 'expedir-itens-comercial',
        mutationFn: (inputs: any) => axiosClient.post(`comercial/pedidos/expedir-itens`, inputs),
        onError: (error) => {
            console.error(error)
            enqueueSnackbar('Erro ao expedir itens!', { variant: 'error' });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(`comercial/detalhar-pedido`)
            queryClient.invalidateQueries(`pesquisar-pedidos`)
            handleClose()
            enqueueSnackbar('Expedição realizada com sucesso!', { variant: 'success' });
        }
    })
    //

    //Funções manipuladoras
    const handleChangeItensParaExpedir = (item: TProdutosDetalhes) => {
        setItensParaExpedir((prevState) => {
            return prevState.map((i) => i.id === item.id ? { ...i, quantidade_para_expedir: item.quantidade_para_expedir, lotes_selecionados: item.lotes_selecionados } : i);
        });
    };

    const handleSubmit = () => {

        if (itensParaExpedir.some((item) => !item.quantidade_para_expedir)) {
            enqueueSnackbar('Favor preencher a quantidade de todos os itens', { variant: 'warning' });
            return;
        }
        if (itensParaExpedir.some((item) => item.exige_lote && !item.lotes_selecionados)) {
            enqueueSnackbar('Favor conferir os lotes de todos os itens que exigem lote', { variant: 'warning' });
            return;
        }

        if (itensParaExpedir.some((item) => item.quantidade_para_expedir && item.quantidade_para_expedir > (item.quantidade - item.quantidade_entregue))) {
            enqueueSnackbar('A quantidade para expedir não pode ser maior que a quantidade comprada', { variant: 'warning' });
            return;
        }

        const params = {
            pedido_id: idPedido,
            pedido_itens: itensParaExpedir.map((item) => {
                const itemParams: any = {
                    pedido_item_id: item.id,
                    quantidade: item.quantidade_para_expedir,
                };
                if (item.exige_lote) {
                    itemParams.lotes_selecionados = item.lotes_selecionados;
                }
                return itemParams;
            }),
        };
        expedirItens(params);
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                PaperComponent={PaperDraggable}
                aria-labelledby="draggable-dialog-title"
                fullWidth
            >
                <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">Expedir itens</DialogTitle>
                <DialogContent>
                    <List sx={{ mt: 2 }}>
                        {itensPerloja.map((item, index) => {
                            return (
                                <Fragment key={index}>
                                    <Typography
                                        mb={1}
                                        variant="subtitle2"
                                    >
                                        <b>{item.loja.id} - {item.loja.nome}</b>
                                    </Typography>
                                    <TableContainer sx={{ mb: 2 }} >
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCellSemBorder sx={{ width: '40%' }}><b>Produto</b></TableCellSemBorder>
                                                    <TableCellSemBorder><b>Controle de lote?</b></TableCellSemBorder>
                                                    <TableCellSemBorder><b>Qtd. a expedir</b></TableCellSemBorder>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {
                                                    item.itens.map((item, index) => {                                
                                                        return (
                                                            <TableRow key={index}>
                                                                <TableCellSemBorder sx={{ width: '40%' }}>{item.nome}</TableCellSemBorder>
                                                                <TableCellSemBorder>{item.exige_lote ? 'Sim' : 'Não'}</TableCellSemBorder>
                                                                <TableCellSemBorder>
                                                                    <Stack direction={'row'} gap={6} alignItems={'center'}>
                                                                        {item.lotes_selecionados && item.lotes_selecionados.length > 0
                                                                            ? item.lotes_selecionados.reduce((acc, lote) => acc + lote.quantidade, 0) 
                                                                            : item.quantidade_para_expedir}
                                                                        <IconButton
                                                                            size='small'
                                                                            onClick={() => setOpenQuantidadeDialog(item)}
                                                                        >
                                                                            <EditIcon fontSize="inherit" />
                                                                        </IconButton>

                                                                    </Stack>
                                                                </TableCellSemBorder>
                                                            </TableRow>
                                                        )
                                                    })
                                                }
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Fragment>
                            )
                        })}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="primary">
                        Fechar
                    </Button>
                    <Button
                        color="primary"
                        variant="contained"
                        disabled={isLoading || itensParaExpedir.some((item) => !item.quantidade_para_expedir)}
                        onClick={handleSubmit}
                    >
                        Gerar expedição
                    </Button>
                </DialogActions>
            </Dialog>

            {
                Boolean(openQuantidadeDialog) && openQuantidadeDialog &&
                <DialogQuantidadeExpedir
                    open={Boolean(openQuantidadeDialog)}
                    data={openQuantidadeDialog}
                    handleClose={() => setOpenQuantidadeDialog(null)}
                    handleChangeQuantidade={(item) => {
                        handleChangeItensParaExpedir(item)
                    }}
                />
            }
        </>
    )
}

interface DialogExpedirItensProps {
    open: boolean;
    handleClose: () => void;
    data: TProdutosDetalhes[]
    idPedido: number
}
